import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import { User } from "lucide-react";
import "bootstrap-icons/font/bootstrap-icons.css";

import { useAuthModal } from "../context/AuthModalContext";
import { useLogoutPrompt } from "../context/LogoutPromptContext";
import { trackCtaClicked } from "../analytics/analytics";
import { ANALYTICS_CTAS } from "../analytics/analytics.constants";
const MENU_WIDTH = 256;
const MENU_GAP = 8;

function getDisplayName(user) {
    const name = String(user?.display_name ?? "").trim();
    if (name) return name;
    const email = String(user?.email ?? "").trim();
    if (email) return email.split("@")[0];
    return "Account";
}

function getBusinessName(profileData) {
    return String(profileData?.name ?? "").trim();
}

export default function UserMenu({ onLoggedOut, className = "" }) {
    const { requestLogout } = useLogoutPrompt();
    const { openAuthModal } = useAuthModal();
    const user = useSelector((state) => state.auth.user);
    const profileData = useSelector((state) => state.profile.data);
    const [open, setOpen] = useState(false);
    const [menuStyle, setMenuStyle] = useState(null);
    const triggerRef = useRef(null);
    const panelRef = useRef(null);

    const displayName = getDisplayName(user);
    const email = String(user?.email ?? "").trim();
    const businessName = getBusinessName(profileData);
    const photoUrl = String(user?.photo_url ?? "").trim();

    const updateMenuPosition = useCallback(() => {
        const trigger = triggerRef.current;
        if (!trigger) return;

        const rect = trigger.getBoundingClientRect();
        const maxLeft = window.innerWidth - MENU_WIDTH - 12;
        const left = Math.min(Math.max(rect.right - MENU_WIDTH, 12), maxLeft);

        setMenuStyle({
            position: "fixed",
            top: rect.bottom + MENU_GAP,
            left,
            width: MENU_WIDTH,
            zIndex: 100,
        });
    }, []);

    useEffect(() => {
        if (!open) return undefined;

        updateMenuPosition();

        const handlePointerDown = (event) => {
            if (triggerRef.current?.contains(event.target)) return;
            if (panelRef.current?.contains(event.target)) return;
            setOpen(false);
        };

        const handleKeyDown = (event) => {
            if (event.key === "Escape") setOpen(false);
        };

        const handleLayoutChange = () => updateMenuPosition();

        document.addEventListener("mousedown", handlePointerDown);
        document.addEventListener("keydown", handleKeyDown);
        window.addEventListener("resize", handleLayoutChange);
        window.addEventListener("scroll", handleLayoutChange, true);

        return () => {
            document.removeEventListener("mousedown", handlePointerDown);
            document.removeEventListener("keydown", handleKeyDown);
            window.removeEventListener("resize", handleLayoutChange);
            window.removeEventListener("scroll", handleLayoutChange, true);
        };
    }, [open, updateMenuPosition]);

    const handleLogout = () => {
        trackCtaClicked(ANALYTICS_CTAS.NAV_LOGOUT);
        setOpen(false);
        requestLogout();
        onLoggedOut?.();
    };

    const handleLogin = () => {
        trackCtaClicked(ANALYTICS_CTAS.NAV_LOGIN);
        setOpen(false);
        onLoggedOut?.();
        openAuthModal({ initialTab: "login" });
    };

    const menuPanel =
        open && menuStyle
            ? createPortal(
                  <div
                      ref={panelRef}
                      role="menu"
                      style={menuStyle}
                      className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg"
                  >
                      {user ? (
                          <>
                              <div className="border-b border-slate-100 px-4 py-3">
                                  <p className="truncate text-sm font-semibold text-slate-900">
                                      {displayName}
                                  </p>
                                  {email ? (
                                      <p className="my-2 mx-3 flex min-w-0 items-center gap-2 text-xs text-slate-500">
                                          <i
                                              className="bi bi-envelope shrink-0 text-slate-400"
                                              aria-hidden
                                          />
                                          <span className="truncate">{email}</span>
                                      </p>
                                  ) : null}
                                  <p className="mb-0 mt-1 mx-3 flex min-w-0 items-center gap-2 text-xs text-slate-600">
                                      <i
                                          className="bi bi-building shrink-0 text-slate-400"
                                          aria-hidden
                                      />
                                      <span className="truncate">
                                          {businessName || "No business profile"}
                                      </span>
                                  </p>
                              </div>

                              <div className="p-2">
                                  <button
                                      type="button"
                                      role="menuitem"
                                      className="btn btn-secondary-app flex w-full items-center justify-center gap-2 py-1 text-sm font-semibold"
                                      onClick={handleLogout}
                                  >
                                      <i className="bi bi-box-arrow-right" aria-hidden />
                                      Log out
                                  </button>
                              </div>
                          </>
                      ) : (
                          <>
                              <div className="border-b border-slate-100 px-4 py-3">
                                  <p className="mb-0 text-sm font-semibold text-slate-900">
                                      Hi Guest
                                  </p>
                              </div>

                              <div className="p-2">
                                  <button
                                      type="button"
                                      role="menuitem"
                                      className="btn btn-dark-app flex w-full items-center justify-center gap-2 text-sm font-semibold"
                                      onClick={handleLogin}
                                  >
                                      <i className="bi bi-box-arrow-in-right" aria-hidden />
                                      Log in
                                  </button>
                              </div>
                          </>
                      )}
                  </div>,
                  document.body
              )
            : null;

    return (
        <>
            <div className={`shrink-0 ${className}`}>
                <button
                    ref={triggerRef}
                    type="button"
                    className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-full border border-slate-200 bg-slate-100 text-slate-600 transition hover:border-slate-300 hover:bg-slate-200/70 focus:outline-none focus:ring-2 focus:ring-slate-900/20"
                    aria-label={user ? "Account menu" : "Guest menu"}
                    aria-haspopup="menu"
                    aria-expanded={open}
                    onClick={() => setOpen((prev) => !prev)}
                >
                    {photoUrl ? (
                        <img
                            src={photoUrl}
                            alt=""
                            className="h-full w-full object-cover"
                            referrerPolicy="no-referrer"
                        />
                    ) : (
                        <User size={18} strokeWidth={2.25} aria-hidden />
                    )}
                </button>
            </div>
            {menuPanel}
        </>
    );
}